import { z } from "zod";

import { relationshipToSubscriberSchema } from "@/packages/shared/src/models/coverage";
import { PersonGender } from "@/packages/shared/src/models/person";
import { eligibilityRequestMemberId } from "@/src/lib/blocks/eligibility-check-inputs";
import { OFFICE_VISIT_SERVICE_TYPE_CODE } from "@/src/lib/clearinghouse/stedi/extract-eligibility-summary";
import { simpleDateSchema } from "@/src/models/common-schema";
import type { Coverage } from "@/src/models/coverage";
import type { StediEligibilityCheck } from "@/src/models/stedi-eligibility-check";
import { StediEligibilityStatusValue } from "@/src/models/stedi-eligibility-status";

export const copayCoverageSchema = z.object({
  id: z.string(),
  subscriberId: z.string().nullable(),
  customerPayer: z.string().nullable(),
  payer: z.string().nullable(),
  plan: z.string().nullable(),
  groupNumber: z.string().nullable(),
  // Older outputs remain readable, but cannot equal a current coverage snapshot for a write.
  effectiveDate: simpleDateSchema.nullable().optional(),
  expirationDate: simpleDateSchema.nullable().optional(),
  subscriberGivenName: z.string().nullable().optional(),
  subscriberFamilyName: z.string().nullable().optional(),
  subscriberDateOfBirth: simpleDateSchema.nullable().optional(),
  subscriberSex: z.enum(PersonGender).nullable().optional(),
  relationshipToSubscriber: relationshipToSubscriberSchema.nullable().optional(),
});

export const coverageCopaySchema = z.object({
  coverage: copayCoverageSchema,
  order: z.number().nullable(),
  patientId: z.string().nullable(),
  checkId: z.string().nullable(),
  checkedAt: z.string().nullable(),
  amount: z.number().nonnegative().multipleOf(0.01).nullable(),
  reviewReason: z.string().nullable(),
  details: z.array(z.string()),
});
export type CoverageCopay = z.infer<typeof coverageCopaySchema>;

const benefitSchema = z
  .object({
    code: z.string(),
    serviceTypeCodes: z.array(z.string()).optional(),
    benefitAmount: z.string().optional(),
    benefitPercent: z.string().optional(),
    coverageLevelCode: z.string().optional(),
    timeQualifierCode: z.string().optional(),
    timeQualifier: z.string().optional(),
    inPlanNetworkIndicatorCode: z.string().optional(),
    additionalInformation: z.array(z.object({ description: z.string().optional() })).optional(),
  })
  .passthrough();

const dollarAmountSchema = z
  .string()
  .trim()
  .regex(/^\d+(?:\.\d{1,2})?$/)
  .transform(Number)
  .pipe(z.number().nonnegative().multipleOf(0.01));

function extractOfficeVisitCopay(response: unknown): Pick<CoverageCopay, "amount" | "reviewReason" | "details"> {
  const parsed = z
    .object({ benefitsInformation: z.array(benefitSchema), errors: z.array(z.unknown()).optional() })
    .safeParse(response);
  const review = (reviewReason: string, details: string[] = []) => ({ amount: null, reviewReason, details });

  if (!parsed.success || parsed.data.errors?.length) return review("Stedi did not return usable copay benefits.");
  const relevant = parsed.data.benefitsInformation.filter((benefit) =>
    benefit.serviceTypeCodes?.includes(OFFICE_VISIT_SERVICE_TYPE_CODE)
  );
  const copays = relevant.filter((benefit) => benefit.code === "B");

  if (!copays.length) return review("No office visit copay supplied by Stedi.");

  const amounts = copays.map((benefit) => dollarAmountSchema.safeParse(benefit.benefitAmount));
  const details = copays.map((benefit, index) => {
    const amount = amounts[index];
    const network = benefit.inPlanNetworkIndicatorCode;

    return [
      amount.success ? `$${amount.data.toFixed(2)}` : "Amount unavailable",
      network === "Y"
        ? "In network"
        : network === "N"
          ? "Out of network"
          : network === "W"
            ? "In and out of network"
            : "Network unknown",
      benefit.timeQualifierCode === "27" ? "Per visit" : (benefit.timeQualifier ?? "Period not specified"),
      benefit.coverageLevelCode === "IND"
        ? "Individual"
        : `Coverage level: ${benefit.coverageLevelCode ?? "not specified"}`,
      ...(benefit.additionalInformation ?? []).map((note) => note.description),
    ]
      .filter(Boolean)
      .join(" · ");
  });

  if (amounts.some((amount) => !amount.success) || copays.some((benefit) => benefit.benefitPercent !== undefined))
    return review("Stedi did not supply a valid dollar copay.", details);
  if (new Set(amounts.map((amount) => amount.data)).size !== 1)
    return review(
      "Stedi returned different copays; confirm the applicable office/specialist and network benefit.",
      details
    );

  // Payer notes and procedure/place/date restrictions can override the numeric benefit.
  const restrictions = [
    "compositeMedicalProcedureIdentifier",
    "eligibilityAdditionalInformation",
    "eligibilityAdditionalInformationList",
    "benefitsAdditionalInformation",
    "benefitsDateInformation",
    "benefitsServiceDelivery",
    "quantityQualifierCode",
    "benefitQuantity",
    "benefitsRelatedEntity",
    "benefitsRelatedEntities",
  ];

  if (
    relevant.some((benefit) => ["6", "7", "8", "I"].includes(benefit.code)) ||
    parsed.data.benefitsInformation.some(
      (benefit) =>
        (!benefit.serviceTypeCodes?.length ||
          benefit.serviceTypeCodes.some((code) => ["30", OFFICE_VISIT_SERVICE_TYPE_CODE].includes(code))) &&
        benefit.additionalInformation?.some((note) => note.description?.trim())
    ) ||
    copays.some((benefit) => benefit.coverageLevelCode !== "IND" || benefit.timeQualifierCode !== "27") ||
    relevant.some((benefit) =>
      restrictions.some((key) => {
        const value = benefit[key];

        return value != null && value !== "" && (!Array.isArray(value) || value.length > 0);
      })
    )
  )
    return review(
      "Confirm the payer's office/specialist, coverage-level, period, or benefit restrictions before entering the copay.",
      details
    );

  const networks = new Set(copays.map((benefit) => benefit.inPlanNetworkIndicatorCode));

  if (
    [...networks].some((network) => !["Y", "N", "W"].includes(network ?? "")) ||
    (!networks.has("W") && !(networks.has("Y") && networks.has("N")))
  )
    return review("Confirm the provider's network status before entering this copay.", details);

  return { amount: amounts[0].data ?? null, reviewReason: null, details };
}

export function stediCoverageCopay(coverage: Coverage, check: StediEligibilityCheck | null): CoverageCopay {
  const request = z
    .object({
      tradingPartnerServiceId: z.string().min(1),
      subscriber: z.object({ memberId: z.string().min(1) }),
    })
    .safeParse(check?.requestJson);
  const matchesCoverage =
    check?.groupId === coverage.groupId &&
    request.success &&
    (!coverage.patientId || check.patientId === coverage.patientId) &&
    request.data.subscriber.memberId ===
      eligibilityRequestMemberId(request.data.tradingPartnerServiceId, coverage.subscriberId?.trim() ?? "");
  const result =
    matchesCoverage && check && !check.checkFailed && check.eligibilityStatus === StediEligibilityStatusValue.ACTIVE
      ? extractOfficeVisitCopay(check.responseJson)
      : { amount: null, reviewReason: "Active coverage was not confirmed by Stedi.", details: [] };

  return {
    coverage: copayCoverageSchema.parse(coverage),
    order: coverage.order,
    patientId: check?.patientId ?? null,
    checkId: check?.id ?? null,
    checkedAt: check?.createdAt ?? null,
    ...result,
  };
}
